import LanguageSelector from 'components/LanguageSelector';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Input } from 'components/ui/input';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import AuthPage from 'components/AuthPage';
import { sendForgotPasswordEmail } from 'api/ForgotPassword/sdk.ts';
import SuccessBox from 'components/SuccessBox';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { t } from 'i18next';
import { Button } from 'components/ui/button.tsx';

const schema = yup.object().shape({
  email: yup
    .string()
    .email(() => t('common.invalidEmailLabel'))
    .required(() => t('common.emailIsRequiredLabel')),
});

interface IForgotPasswordForm {
  email: string;
}

export const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState<boolean>(false);

  const {
    handleSubmit,
    formState: { isValid, isSubmitting },
    control,
    reset,
  } = useForm<IForgotPasswordForm>({
    defaultValues: { email: '' },
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const onSubmit: SubmitHandler<IForgotPasswordForm> = async ({ email }) => {
    try {
      setSubmitted(true);
      await sendForgotPasswordEmail({ email });
    } catch (error) {
      console.error(error);
    } finally {
      reset();
    }
  };

  return (
    <AuthPage>
      <div className='flex flex-col justify-center w-full'>
        {submitted ? (
          <div className={'flex flex-col gap-[24px]'}>
            <SuccessBox text={t('resetPassword.successTitle')}>
              <div className='flex flex-col gap-[8px]'>
                <div className='flex gap-[8px]'>
                  <InformationCircleIcon className='size-[20px] text-primary' />
                  <div className='flex-1 font-[MuseoSansCyrl] text-[14px]'>{t('resetPassword.linkSentTitle')}</div>
                </div>
                <div className='flex gap-[8px]'>
                  <InformationCircleIcon className='size-[20px] text-primary' />
                  <div className='flex-1 font-[MuseoSansCyrl] text-[14px] max-w-[400px]'>
                    {t('resetPassword.linkSentDescription')}
                  </div>
                </div>
              </div>
            </SuccessBox>
            <Button
              onClick={() => setSubmitted(false)}
              className={'self-center w-full md:w-fit font-semibold'}
              disabled={isSubmitting}
            >
              {t('resetPassword.resendResetLinkButton')}
            </Button>
          </div>
        ) : (
          <>
            <div className='w-full pb-[24px] flex justify-between items-center'>
              <h1 className='bold text-grey800 font-[DKLiquidEmbrace] text-3xl text-center md:text-left'>
                {t('login.forgotPassword')}
              </h1>
              <LanguageSelector />
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className={'flex flex-col gap-[24px]'}>
              <Controller
                control={control}
                name='email'
                render={({ field, fieldState }) => (
                  <Input
                    type='email'
                    label={t('forgotPassword.emailLabel')}
                    placeholder={t('forgotPassword.emailPlaceholder')}
                    error={fieldState.error}
                    isTouched={fieldState.isTouched}
                    {...field}
                  />
                )}
              />
              <Button type='submit' className={'w-full md:w-fit font-semibold'} disabled={!isValid || isSubmitting}>
                {t('common.sendEmail')}
              </Button>
            </form>
          </>
        )}
      </div>
    </AuthPage>
  );
};
